import { useEffect, useRef, useState } from "react";

import { useAppFeatures } from "@/hooks/useAppFeatures";

import useSsoLogin from "./useSsoLogin";

/**
 * @param shouldStart caller's own "unauthenticated and done checking" signal.
 * @returns `isRedirecting` — true while a redirect to the IdP is pending, so the
 * caller should render a loading state rather than its unauthenticated UI.
 */
export const useSsoAutoStart = (shouldStart: boolean) => {
  const { isLokasiSsoAuthEnabled } = useAppFeatures();
  const { startSso } = useSsoLogin();

  // Set when startSso can't proceed (no provider, or the provider fetch failed).
  const [providerAvailable, setProviderAvailable] = useState(true);

  // One attempt per mount. `startSso` navigates away on success, so a second
  // attempt would only ever be a redirect loop (e.g. the IdP bouncing us back
  // unauthenticated).
  const hasAttempted = useRef(false);

  const isRedirecting =
    isLokasiSsoAuthEnabled && providerAvailable && shouldStart;

  useEffect(() => {
    if (!isRedirecting || hasAttempted.current) return;

    hasAttempted.current = true;
    startSso().catch(() => setProviderAvailable(false));
  }, [isRedirecting, startSso]);

  return { isRedirecting };
};

export default useSsoAutoStart;
